import { useRef } from "react";
import useSWR, { useSWRConfig } from "swr";
import {
  IngestJobsService,
  IngestJob,
  ingestJobCacheKey,
  ingestJobsCacheKey,
  isJobActive,
  TERMINAL_STATUSES,
} from "@/services/ingest-jobs.service";

// Poll every 2.5s while any run is active, then stop.
const ACTIVE_POLL_MS = 2500;

/**
 * Live list of ingestion jobs for a catalogue. Polls only while a run is active
 * and, when a run flips to a terminal state, refreshes the product-list SWR keys
 * so newly imported products appear without a manual reload.
 */
export function useIngestJobs(catalogueId?: string | null) {
  const { mutate: globalMutate } = useSWRConfig();
  // Job status is an open string on the wire (relayed verbatim from the
  // product-ingest service); TERMINAL_STATUSES does the narrowing.
  const prevStatuses = useRef<Record<string, string>>({});

  const swr = useSWR<IngestJob[]>(
    catalogueId ? ingestJobsCacheKey(catalogueId) : null,
    () => IngestJobsService.list(catalogueId as string),
    {
      refreshInterval: (latest?: IngestJob[]) => (latest && latest.some(isJobActive) ? ACTIVE_POLL_MS : 0),
      revalidateOnFocus: true,
      onSuccess: (jobs) => {
        for (const job of jobs) {
          const prev = prevStatuses.current[job.id];
          if (prev && !TERMINAL_STATUSES.has(prev) && TERMINAL_STATUSES.has(job.status)) {
            globalMutate(
              (key) => Array.isArray(key) && key[0] === "products",
            );
          }
          prevStatuses.current[job.id] = job.status;
        }
      },
    },
  );

  const jobs = swr.data ?? [];
  return {
    jobs,
    activeJobs: jobs.filter(isJobActive),
    isLoading: swr.isLoading,
    error: swr.error,
    mutate: swr.mutate,
  };
}

/** Live single ingestion job (used by the Tasks page). Polls only while active. */
export function useIngestJob(catalogueId?: string | null, jobId?: string | null) {
  const swr = useSWR<IngestJob | null>(
    catalogueId && jobId ? ingestJobCacheKey(catalogueId, jobId) : null,
    () => IngestJobsService.get(catalogueId as string, jobId as string),
    {
      refreshInterval: (latest?: IngestJob | null) => (latest && isJobActive(latest) ? ACTIVE_POLL_MS : 0),
      revalidateOnFocus: true,
    },
  );
  return { job: swr.data ?? null, isLoading: swr.isLoading, error: swr.error };
}
